import { useGetPracticeQuery, useUpdatePracticeMutation } from "api";
import Site from "api/types/models/site";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Toggle,
  Modal,
  Tabs,
  NotifierNotification,
  ConfirmationModal,
} from "@mwi/ui";
import useErrorHandler from "hooks/useErrorHandler";
import { useEffect, useState } from "react";
import { useNotifier } from "react-headless-notifier";
import { useForm } from "react-hook-form";

type Inputs = {
  merlin_id: string;
  subdomain: string;
  display_name: string;
  email: string;
  phone: string;
  address: string;
  website_link: string;
  privacy_link: string;
};

const EditPracticeModal = ({
  isOpen,
  onClose,
  practiceId,
}: {
  isOpen: boolean;
  onClose: () => void;
  practiceId?: number;
}) => {
  const { notify } = useNotifier();
  const errorHandler = useErrorHandler();

  const [selectedTab, setSelectedTab] = useState<"details" | "locations">(
    "details",
  );
  const [locations, setLocations] = useState<Site[]>([]);
  const [enabledLocations, setEnabledLocations] = useState<string[]>([]);
  const [openSubdomainConfirmationModal, setOpenSubdomainConfirmationModal] =
    useState(false);

  const {
    handleSubmit,
    register,
    setError,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<Inputs>();

  const [
    updatePractice,
    { data: updatedPractice, isLoading: isLoadingUpdate, error },
  ] = useUpdatePracticeMutation();

  const { data: practice } = useGetPracticeQuery(practiceId ?? 0, {
    skip: practiceId === undefined,
  });

  useEffect(() => {
    if (error) {
      errorHandler(error, setError);
    }
  }, [error]);

  useEffect(() => {
    if (practice) {
      setValue("merlin_id", practice.merlin_id!);
      setValue("subdomain", practice.subdomain);
      setValue("display_name", practice.display_name);
      setValue("phone", practice.phone);
      setValue("email", practice.email);
      setValue("address", practice.address);
      setValue("website_link", practice.website_link!);
      setValue("privacy_link", practice.privacy_link!);
      setLocations(practice.locations!);
      setEnabledLocations(
        practice.locations!.filter((l) => l.enabled).map((l) => l.id),
      );
    }
  }, [practice, practiceId]);

  useEffect(() => {
    if (updatedPractice) {
      onClose();
      notify(
        <NotifierNotification
          title="Success!"
          message="Practice has been updated"
        />,
      );
    }
  }, [updatedPractice]);

  const afterLeave = () => {
    reset();
    setEnabledLocations([]);
    setSelectedTab("details");
    setOpenSubdomainConfirmationModal(false);
  };

  const handleUpdatePractice = () => {
    setOpenSubdomainConfirmationModal(false);
    updatePractice({
      id: practiceId!,
      body: {
        merlin_id: getValues("merlin_id"),
        subdomain: getValues("subdomain"),
        display_name: getValues("display_name"),
        email: getValues("email"),
        phone: getValues("phone"),
        address: getValues("address"),
        website_link: getValues("website_link"),
        privacy_link: getValues("privacy_link"),
        is_active: practice?.is_active,
        locations: enabledLocations,
      },
    });
  };

  const triggerSubmit = () => {
    getValues("subdomain") !== practice?.subdomain
      ? setOpenSubdomainConfirmationModal(true)
      : handleUpdatePractice();
  };

  const toggleLocation = (id: string) => {
    enabledLocations.includes(id)
      ? setEnabledLocations(enabledLocations.filter((l) => l !== id))
      : setEnabledLocations([...enabledLocations, id]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onClose()}
      afterLeave={afterLeave}
      className="tablet:!max-w-[775px]"
    >
      <Modal.Header title="Edit Vet Practice" />

      <form onSubmit={handleSubmit(triggerSubmit)}>
        <Tabs className="mb-8">
          <Tabs.Item
            title="Details"
            isActive={selectedTab === "details"}
            onClick={() => setSelectedTab("details")}
          />
          <Tabs.Item
            title="Locations"
            isActive={selectedTab === "locations"}
            onClick={() => setSelectedTab("locations")}
          />
        </Tabs>

        {selectedTab === "details" && (
          <>
            <FormGroup error={errors?.merlin_id?.message}>
              <Label>Tenant ID</Label>
              <Input {...register("merlin_id")} />
            </FormGroup>

            <FormGroup error={errors?.subdomain?.message}>
              <Label>Subdomain</Label>
              <Input {...register("subdomain")} />
            </FormGroup>

            <FormGroup error={errors?.display_name?.message}>
              <Label>Practice Name</Label>
              <Input {...register("display_name")} />
            </FormGroup>

            <FormGroup error={errors?.address?.message}>
              <Label>Practice Address</Label>
              <Input {...register("address")} />
            </FormGroup>

            <FormGroup error={errors?.phone?.message}>
              <Label>Practice Phone</Label>
              <Input type="tel" maxChars={11} {...register("phone")} />
            </FormGroup>

            <FormGroup error={errors?.email?.message}>
              <Label>Practice Email</Label>
              <Input {...register("email")} />
            </FormGroup>

            <FormGroup error={errors?.website_link?.message}>
              <Label>Practice Website Link</Label>
              <Input {...register("website_link")} />
            </FormGroup>

            <FormGroup error={errors?.privacy_link?.message}>
              <Label>Practice Privacy Policy Link</Label>
              <Input {...register("privacy_link")} />
            </FormGroup>
          </>
        )}

        {selectedTab === "locations" && (
          <>
            <p className="mb-10">Configure your locations</p>

            <div className="flex flex-col gap-6">
              {locations.map((location) => (
                <div className="flex" key={location.id}>
                  <p className="flex-grow">{location.name}</p>
                  <Toggle
                    value={enabledLocations.includes(location.id)}
                    onChange={() => toggleLocation(location.id)}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        <Button type="submit" loading={isLoadingUpdate} className="mt-10">
          Save
        </Button>
      </form>

      <ConfirmationModal
        title="Are you sure?"
        description="Changing your subdomain will break any links that contain the old subdomain and will need to be updated to the new one."
        isOpen={openSubdomainConfirmationModal}
        onClose={() => setOpenSubdomainConfirmationModal(false)}
        onConfirm={() => handleUpdatePractice()}
      />
    </Modal>
  );
};

export default EditPracticeModal;
